import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
 Proposal,
 ProposalFormData,
 Lead,
 Vehicle,
 FinancialCharge,
} from "../../types";
import { Button } from "../ui";
import { useLeads } from "../../hooks/useLeads";
import { useVehicles } from "../../hooks/useVehicles";
import { useBenefits } from "../../hooks/useBenefits";
import { useFinancialCharges } from "../../hooks/useFinancialCharges";
import { useCatalog } from "../../hooks/useCatalog";
import { useVehicleOptions } from "../../hooks/useVehicleOptions";
import {
 formatCurrency,
 parseCurrency,
 formatPercentageInput,
 parsePercentage,
} from "../../utils/formatters";
import { cn } from "../../utils/cn";
import { ClientSection } from "./forms/ClientSection";
import { VehicleSection } from "./forms/VehicleSection";
import { FinancialProposalSection } from "./forms/FinancialProposalSection";
import { TradeVehiclesSection } from "./forms/TradeVehiclesSection";
import { PaymentsSection } from "./forms/PaymentsSection";
import { ProposalDetailsSection } from "./forms/ProposalDetailsSection";
import { FinancialSummary } from "./forms/FinancialSummary";
import { apiService } from "../../services/api";
import {
 User,
 DollarSign,
 ArrowLeftRight,
 FileText,
 Check,
 ChevronLeft,
 ChevronRight,
} from "lucide-react";

interface ProposalFormProps {
 proposal?: Proposal | null;
 onSubmit: (data: ProposalFormData) => void;
 onCancel: () => void;
 isSubmitting?: boolean;
 preSelectedLeadId?: string;
 preSelectedVehicleId?: string;
}

const STEPS = [
 { id: "client-vehicle", label: "Cliente e Veículo", shortLabel: "Cliente", icon: User },
 { id: "financial", label: "Proposta Financeira", shortLabel: "Financeiro", icon: DollarSign },
 { id: "trade-payment", label: "Troca e Pagamentos", shortLabel: "Troca", icon: ArrowLeftRight },
 { id: "details", label: "Detalhes e Resumo", shortLabel: "Resumo", icon: FileText },
] as const;

export const ProposalForm: React.FC<ProposalFormProps> = ({
 proposal,
 onSubmit,
 onCancel,
 isSubmitting = false,
 preSelectedLeadId,
 preSelectedVehicleId,
}) => {
 const { t } = useTranslation();
 const { leads } = useLeads();
 const { vehicles, fetchVehicles } = useVehicles();
 const { benefits, fetchBenefits } = useBenefits();
 const { charges, fetchCharges } = useFinancialCharges();
 const { brands, models, fetchBrands, fetchBrandModels } = useCatalog();
 const { options: vehicleOptions } = useVehicleOptions();

 const [currentStep, setCurrentStep] = useState(0);
 const [vehiclesLoaded, setVehiclesLoaded] = useState(false);

 const [formData, setFormData] = useState<ProposalFormData>({
 leadId: proposal?.leadId || preSelectedLeadId || "",
 vehicleId: proposal?.vehicleId || preSelectedVehicleId || "",
 proposedPrice: proposal?.value || proposal?.proposedPrice || 0,
 finalPrice: 0,
 notes: proposal?.notes || "",
 validUntil: proposal?.validUntil || "",
 deliveryDate: proposal?.deliveryDate || "",
 assignedTo: proposal?.assignedTo || "",
 });

 const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
 const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
 const [discount, setDiscount] = useState(0);
 const [discountPercentage, setDiscountPercentage] = useState(0);
 const [proposedPriceFormatted, setProposedPriceFormatted] = useState("");
 const [priceInputType, setPriceInputType] = useState<"value" | "discount">("value");
 const [discountInputFormatted, setDiscountInputFormatted] = useState("");
 const [priceError, setPriceError] = useState<string>("");
 const [selectedBenefits, setSelectedBenefits] = useState<string[]>([]);
 const [selectedCharges, setSelectedCharges] = useState<string[]>([]);
 const [benefitsWithFullDiscount, setBenefitsWithFullDiscount] = useState<string[]>([]);
 const [chargeDiscounts, setChargeDiscounts] = useState<Record<string, number>>({});
 const [chargesWithDiscountEnabled, setChargesWithDiscountEnabled] = useState<string[]>([]);
 const [savedPaymentPlans, setSavedPaymentPlans] = useState<
 Array<{
 paymentMethodId: string;
 optionId?: string;
 value: number;
 installments?: number;
 date?: string;
 }>
 >([]);
 const [tempPaymentMethod, setTempPaymentMethod] = useState<string>("");
 const [tempPaymentOption, setTempPaymentOption] = useState<string>("");
 const [tempPaymentAmountFormatted, setTempPaymentAmountFormatted] =
 useState<string>("");
 const [paymentForms, setPaymentForms] = useState<
 Array<{
 id: string;
 name: string;
 description?: string;
 options?: Array<{
 id: string;
 paymentFormId: string;
 name: string;
 description?: string;
 installments: number;
 interestRate?: string | number;
 isActive?: boolean;
 }>;
 }>
 >([]);
 const [isLoadingPaymentData, setIsLoadingPaymentData] = useState(false);
 const [tradeVehicles, setTradeVehicles] = useState<ProposalFormData["tradeVehicles"] | null>(null);
 const [savedTradeVehicles, setSavedTradeVehicles] = useState<ProposalFormData["tradeVehicles"] | null>(null);
 const [vehicleValueFormatted, setVehicleValueFormatted] = useState<string>("");

 // ── Effects ─────────────────────────────────────────────────────────────────

 useEffect(() => {
 if (formData.leadId) {
 const lead = leads.find((l) => l.id === formData.leadId);
 setSelectedLead(lead || null);
 }
 }, [formData.leadId, leads]);

 useEffect(() => {
 if (!vehiclesLoaded && vehicles.length === 0) {
 fetchVehicles({ limit: 100 });
 setVehiclesLoaded(true);
 }
 }, []); // eslint-disable-line react-hooks/exhaustive-deps

 useEffect(() => {
 if (benefits.length === 0) fetchBenefits({ limit: 100 });
 if (charges.length === 0) fetchCharges({ limit: 100 });
 }, []); // eslint-disable-line react-hooks/exhaustive-deps

 useEffect(() => {
 if (charges.length > 0 && selectedCharges.length === 0) {
 const requiredIds = charges.filter((c) => c.isRequired && c.isActive).map((c) => c.id);
 if (requiredIds.length > 0) setSelectedCharges(requiredIds);
 } else if (charges.length > 0) {
 const requiredIds = charges.filter((c) => c.isRequired && c.isActive).map((c) => c.id);
 const missing = requiredIds.filter((id) => !selectedCharges.includes(id));
 if (missing.length > 0) setSelectedCharges([...selectedCharges, ...missing]);
 }
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [charges]);

 const [hasTriedFetchBrands, setHasTriedFetchBrands] = React.useState(false);

 useEffect(() => {
 if (savedTradeVehicles && savedTradeVehicles.length > 0) {
 const hasBrandIds = savedTradeVehicles.some((v) => v.brandId);
 if (hasBrandIds && brands.length === 0 && !hasTriedFetchBrands) {
 setHasTriedFetchBrands(true);
 fetchBrands().catch(() => {});
 }
 }
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [savedTradeVehicles, brands.length]);

 const [triedFetchModelsForBrands, setTriedFetchModelsForBrands] =
 React.useState<Set<string>>(new Set());

 useEffect(() => {
 if (savedTradeVehicles && savedTradeVehicles.length > 0 && brands.length > 0) {
 const uniqueBrandIds = [
 ...new Set(
 savedTradeVehicles.map((v) => v.brandId).filter((id) => id && id.trim() !== "")
 ),
 ];
 uniqueBrandIds.forEach((brandId) => {
 if (brandId && !triedFetchModelsForBrands.has(brandId)) {
 const brandModels = models.filter((m) => m.brandId === brandId);
 if (brandModels.length === 0) {
 setTriedFetchModelsForBrands((prev) => new Set(prev).add(brandId));
 fetchBrandModels(brandId).catch(() => {});
 }
 }
 });
 }
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [savedTradeVehicles, brands.length, models.length]);

 useEffect(() => {
 const loadPaymentData = async () => {
 setIsLoadingPaymentData(true);
 try {
 const formsResponse = await apiService.getPaymentForms();
 if (Array.isArray(formsResponse)) setPaymentForms(formsResponse);
 } catch (error) {
 console.error("Erro ao carregar dados de pagamento:", error);
 } finally {
 setIsLoadingPaymentData(false);
 }
 };
 loadPaymentData();
 }, []);

 useEffect(() => {
 if (formData.vehicleId && vehicles.length > 0) {
 const vehicle = vehicles.find((v) => v.id === formData.vehicleId);
 if (vehicle) {
 setSelectedVehicle(vehicle);
 if (!proposal && formData.proposedPrice === 0) {
 const vehiclePrice = getVehiclePrice(vehicle);
 if (vehiclePrice > 0) {
 setFormData((prev) => ({ ...prev, proposedPrice: vehiclePrice }));
 setProposedPriceFormatted(formatCurrency(vehiclePrice));
 setVehicleValueFormatted(formatCurrency(vehiclePrice));
 }
 }
 }
 }
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [formData.vehicleId, vehicles.length]);

 useEffect(() => {
 const raw = proposal?.value ?? proposal?.proposedPrice;
 if (raw == null) return;
 const n = typeof raw === "number" ? raw : parseFloat(String(raw));
 if (!Number.isFinite(n) || n <= 0) return;
 setProposedPriceFormatted(formatCurrency(n));
 setVehicleValueFormatted(formatCurrency(n));
 }, [proposal]);

 useEffect(() => {
 if (proposal) {
 if (proposal.benefitUsages && proposal.benefitUsages.length > 0) {
 const benefitIds = proposal.benefitUsages.map((bu) => bu.benefitId);
 setSelectedBenefits(benefitIds);
 const fullDiscountBenefits = proposal.benefitUsages
 .filter((bu) => parseFloat(bu.value.toString()) === 0)
 .map((bu) => bu.benefitId);
 setBenefitsWithFullDiscount(fullDiscountBenefits);
 }

 if (proposal.chargeUsages && proposal.chargeUsages.length > 0) {
 const chargeIds = proposal.chargeUsages.map((cu) => cu.chargeId);
 const requiredIds = charges.filter((c) => c.isRequired && c.isActive).map((c) => c.id);
 setSelectedCharges([...new Set([...chargeIds, ...requiredIds])]);
 const discounts: Record<string, number> = {};
 const enabledDiscounts: string[] = [];
 proposal.chargeUsages.forEach((cu) => {
 if (cu.discountPercentage !== undefined && cu.discountPercentage !== null) {
 const d =
 typeof cu.discountPercentage === "string"
 ? parseFloat(cu.discountPercentage)
 : cu.discountPercentage;
 if (d > 0) {
 discounts[cu.chargeId] = d;
 enabledDiscounts.push(cu.chargeId);
 }
 }
 });
 setChargeDiscounts(discounts);
 setChargesWithDiscountEnabled(enabledDiscounts);
 }

 if (proposal.payments && proposal.payments.length > 0) {
 const plans = proposal.payments.map((payment) => {
const paymentValue = payment.downPayment
      ? typeof payment.downPayment === "string"
        ? parseFloat(payment.downPayment)
        : typeof payment.downPayment === "number"
        ? payment.downPayment
        : 0
      : 0;
 return {
 paymentMethodId: payment.paymentFormId,
 optionId: payment.paymentOptionId || undefined,
 value: Number(paymentValue) || 0,
 installments: payment.installments,
 date: payment.date || new Date().toISOString().split("T")[0],
 };
 });
 setSavedPaymentPlans(plans);
 }

 if (proposal.tradeVehicles && proposal.tradeVehicles.length > 0) {
 setSavedTradeVehicles(proposal.tradeVehicles as any);
 } else if ((proposal as any).tradeVehicle) {
 setSavedTradeVehicles([(proposal as any).tradeVehicle]);
 }
 }
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [proposal]);

 // ── Helpers ──────────────────────────────────────────────────────────────────

 const getVehiclePrice = (vehicle: any): number => {
 if (!vehicle) return 0;
 let price = 0;
 if (vehicle.salePrice)
 price = typeof vehicle.salePrice === "string" ? parseFloat(vehicle.salePrice) || 0 : Number(vehicle.salePrice) || 0;
 else if (vehicle.price)
 price = typeof vehicle.price === "string" ? parseFloat(vehicle.price) || 0 : Number(vehicle.price) || 0;
 if (price > 1000000) price = price / 100;
 return isNaN(price) ? 0 : price;
 };

 const getVehicleMinimumPrice = (vehicle: any): number => {
 if (!vehicle) return 0;
 let minPrice = 0;
 if (vehicle.minimumSalePrice)
 minPrice = typeof vehicle.minimumSalePrice === "string" ? parseFloat(vehicle.minimumSalePrice) || 0 : Number(vehicle.minimumSalePrice) || 0;
 else if (vehicle.minimumPrice)
 minPrice = typeof vehicle.minimumPrice === "string" ? parseFloat(vehicle.minimumPrice) || 0 : Number(vehicle.minimumPrice) || 0;
 else if (vehicle.minPrice)
 minPrice = typeof vehicle.minPrice === "string" ? parseFloat(vehicle.minPrice) || 0 : Number(vehicle.minPrice) || 0;
 else if (vehicle.pricing?.sale?.minimumPrice)
 minPrice = typeof vehicle.pricing.sale.minimumPrice === "string" ? parseFloat(vehicle.pricing.sale.minimumPrice) || 0 : Number(vehicle.pricing.sale.minimumPrice) || 0;
 else if (vehicle.pricing?.sale?.minimumSalePrice)
 minPrice = typeof vehicle.pricing.sale.minimumSalePrice === "string" ? parseFloat(vehicle.pricing.sale.minimumSalePrice) || 0 : Number(vehicle.pricing.sale.minimumSalePrice) || 0;
 if (minPrice > 1000000) minPrice = minPrice / 100;
 return isNaN(minPrice) ? 0 : minPrice;
 };

 const getVehicleName = (vehicle: any): string => {
 if (vehicle.name) return vehicle.name;
 const brand = vehicle.brand?.name || vehicle.brandName || vehicle.brand || "";
 const model = vehicle.model?.name || vehicle.modelName || vehicle.model || "";
 const year = vehicle.modelYear || vehicle.year || "";
 return `${brand} ${model} ${year}`.trim();
 };

 // ── Calculations ─────────────────────────────────────────────────────────────

 const calculateBenefitsTotal = (): number => {
 const total = selectedBenefits.reduce((sum, benefitId) => {
 const benefit = benefits.find((b) => b.id === benefitId);
 if (!benefit) return sum;
 if (benefitsWithFullDiscount.includes(benefitId)) return sum;
 const raw: string | number = benefit.value as string | number;
 let val =
 typeof raw === "string"
 ? parseFloat(raw.replace(/[^\d,.-]/g, "").replace(",", ".")) || 0
 : Number(raw) || 0;
 if (isNaN(val) || !isFinite(val)) val = 0;
 return sum + val;
 }, 0);
 return isNaN(total) || !isFinite(total) ? 0 : total;
 };

const calculateChargesTotal = (): number => {
  const proposedPrice = formData.proposedPrice as string | number | undefined;
  const basePrice =
    typeof proposedPrice === "string"
      ? parseFloat(proposedPrice.replace(/[^\d,.-]/g, "").replace(",", ".")) || 0
      : Number(proposedPrice) || 0;
 const benefitsTotal = Number(calculateBenefitsTotal()) || 0;
 const priceForPct = basePrice + benefitsTotal;

 const total = selectedCharges.reduce((sum, chargeId) => {
 const charge = charges.find((c) => c.id === chargeId && c.isActive);
 if (!charge) return sum;
 let chargeValue = 0;
 if (charge.isPercentage && priceForPct > 0) {
 const pct =
 typeof charge.percentage === "string"
 ? parseFloat((charge.percentage as string).replace(/[^\d,.-]/g, "").replace(",", ".")) || 0
 : Number(charge.percentage) || 0;
 chargeValue = (priceForPct * pct) / 100;
 } else {
 chargeValue =
 typeof charge.amount === "string"
 ? parseFloat((charge.amount as string).replace(/[^\d,.-]/g, "").replace(",", ".")) || 0
 : Number(charge.amount) || 0;
 }
 const discount =
 typeof chargeDiscounts[chargeId] === "string"
 ? parseFloat(String(chargeDiscounts[chargeId]).replace(/[^\d,.-]/g, "").replace(",", ".")) || 0
 : Number(chargeDiscounts[chargeId]) || 0;
 if (discount > 0) chargeValue = chargeValue * (1 - discount / 100);
 if (isNaN(chargeValue) || !isFinite(chargeValue)) chargeValue = 0;
 return sum + chargeValue;
 }, 0);
 return isNaN(total) || !isFinite(total) ? 0 : total;
 };

 const getChargeOriginalValue = (charge: FinancialCharge): number => {
 if (!charge) return 0;
 const basePrice = Number(formData.proposedPrice) || 0;
 const benefitsTotal = calculateBenefitsTotal();
 const priceForPct = basePrice + benefitsTotal;
 if (charge.isPercentage && priceForPct > 0) {
 return (priceForPct * (Number(charge.percentage) || 0)) / 100;
 }
 return Number(charge.amount) || 0;
 };

 const getChargeValue = (charge: FinancialCharge): number => {
 if (!charge) return 0;
 let val = getChargeOriginalValue(charge);
 const d = chargeDiscounts[charge.id] || 0;
 if (d > 0) val = val * (1 - d / 100);
 return isNaN(val) ? 0 : val;
 };

 const calculateTradeVehiclesTotal = (): number => {
 const veh = savedTradeVehicles || [];
 if (veh.length === 0) return 0;
 return veh.reduce((total, vehicle) => {
 const offeredValue =
 typeof vehicle.offeredValue === "string"
 ? parseFloat((vehicle.offeredValue as string).replace(/[^\d,.-]/g, "").replace(",", ".")) || 0
 : Number(vehicle.offeredValue) || 0;
 const expensesTotal =
 vehicle.expenses?.reduce((sum, e) => {
 const ev =
 typeof e.value === "string"
 ? parseFloat((e.value as string).replace(/[^\d,.-]/g, "").replace(",", ".")) || 0
 : Number(e.value) || 0;
 return sum + ev;
 }, 0) || 0;
 const vehicleTotal = offeredValue - expensesTotal;
 return total + (isNaN(vehicleTotal) ? 0 : vehicleTotal);
 }, 0);
 };

 const calculateFinalPrice = (): number => {
 const basePrice =
 typeof formData.proposedPrice === "string"
 ? parseFloat((formData.proposedPrice as string).replace(/[^\d,.-]/g, "").replace(",", ".")) || 0
 : Number(formData.proposedPrice) || 0;
 const finalPrice =
 basePrice +
 (Number(calculateBenefitsTotal()) || 0) +
 (Number(calculateChargesTotal()) || 0) -
 (Number(calculateTradeVehiclesTotal()) || 0);
 return Math.max(0, isNaN(finalPrice) ? 0 : finalPrice);
 };

 const calculateTotalPaid = (): number => {
 const total = savedPaymentPlans.reduce((sum, plan) => {
 let value =
 typeof plan.value === "string"
 ? parseFloat((plan.value as string).replace(/[^\d,.-]/g, "").replace(",", ".")) || 0
 : Number(plan.value) || 0;
 if (isNaN(value) || !isFinite(value)) value = 0;
 return sum + value;
 }, 0);
 return isNaN(total) || !isFinite(total) ? 0 : total;
 };

 const calculateRemainingAmount = (): number => {
 const remaining = (Number(calculateFinalPrice()) || 0) - (Number(calculateTotalPaid()) || 0);
 return Math.max(0, isNaN(remaining) || !isFinite(remaining) ? 0 : remaining);
 };

 const FINANCIAL_BALANCE_EPSILON = 0.01;
 const isFinanciallyBalanced = () =>
 calculateRemainingAmount() <= FINANCIAL_BALANCE_EPSILON;

 const calculatePaymentWithInterest = (
 baseValue: number,
 installments: number,
 interestRate?: string | number
 ): { totalWithInterest: number; installmentValue: number } => {
 if (!installments || installments <= 0)
 return { totalWithInterest: baseValue, installmentValue: baseValue };
 const rate = interestRate
 ? typeof interestRate === "string"
 ? parseFloat(interestRate) || 0
 : interestRate
 : 0;
 const totalWithInterest = rate > 0 ? baseValue * (1 + rate / 100) : baseValue;
 return { totalWithInterest, installmentValue: totalWithInterest / installments };
 };

 // ── Handlers ─────────────────────────────────────────────────────────────────

 const handleInputChange = (field: keyof ProposalFormData, value: string | number) => {
 setFormData((prev) => ({ ...prev, [field]: value }));
 };

 const validateProposedPrice = (proposedPrice: number) => {
 if (!selectedVehicle) {
 setPriceError("");
 return;
 }
 const minimumPrice = getVehicleMinimumPrice(selectedVehicle);
 if (minimumPrice > 0 && proposedPrice < minimumPrice) {
 setPriceError(t("proposals.form.invalidNegotiatedPrice"));
 } else {
 setPriceError("");
 }
 };

 const handleProposedPriceChange = (formattedValue: string) => {
 setProposedPriceFormatted(formattedValue);
 const numericValue = parseCurrency(formattedValue);
 validateProposedPrice(numericValue);
 handleInputChange("proposedPrice", numericValue);
 };

 const handleVehicleValueChange = (formattedValue: string) => {
 setVehicleValueFormatted(formattedValue);
 };

 const handleApplyAdvertisedVehicleValue = () => {
 if (!selectedVehicle) return;
 const advertised = getVehiclePrice(selectedVehicle);
 if (advertised <= 0) return;
 setVehicleValueFormatted(formatCurrency(advertised));
 };

 const handleDiscountInputChange = (inputValue: string) => {
 if (inputValue === "" || inputValue === "%") {
 setDiscountInputFormatted("");
 if (selectedVehicle) {
 const originalPrice = getVehiclePrice(selectedVehicle);
 if (originalPrice > 0) {
 handleInputChange("proposedPrice", originalPrice);
 setProposedPriceFormatted(formatCurrency(originalPrice));
 }
 }
 setPriceError("");
 return;
 }
 let discountPercent = parsePercentage(inputValue);
 if (isNaN(discountPercent) || discountPercent < 0) {
 setDiscountInputFormatted("");
 setPriceError("");
 return;
 }
 if (discountPercent > 100) {
 setDiscountInputFormatted(inputValue.trim() ? inputValue : "");
 setPriceError(t("proposals.form.invalidNegotiatedPrice"));
 return;
 }
 if (!selectedVehicle) {
 setDiscountInputFormatted(formatPercentageInput(discountPercent));
 setPriceError("");
 return;
 }
 const originalPrice = getVehiclePrice(selectedVehicle);
 setDiscountInputFormatted(formatPercentageInput(discountPercent));
 setPriceError("");
 if (originalPrice > 0) {
 const proposedPrice = originalPrice * (1 - discountPercent / 100);
 validateProposedPrice(proposedPrice);
 handleInputChange("proposedPrice", proposedPrice);
 const formatted = formatCurrency(proposedPrice);
 setProposedPriceFormatted(formatted);
 }
 };

 const isPriceInvalid = (): boolean => {
 if (!selectedVehicle) return false;
 const minimumPrice = getVehicleMinimumPrice(selectedVehicle);
 return minimumPrice > 0 && formData.proposedPrice < minimumPrice;
 };

 useEffect(() => {
 if (selectedVehicle && formData.proposedPrice > 0) {
 const vehiclePrice = getVehiclePrice(selectedVehicle);
 if (vehiclePrice > 0) {
 const discountValue = vehiclePrice - formData.proposedPrice;
 const discountPercent = (discountValue / vehiclePrice) * 100;
 setDiscount(discountValue);
 setDiscountPercentage(discountPercent);
 if (priceInputType === "discount")
 setDiscountInputFormatted(formatPercentageInput(discountPercent));
 }
 } else {
 setDiscount(0);
 setDiscountPercentage(0);
 if (priceInputType === "discount") setDiscountInputFormatted("");
 }
 }, [formData.proposedPrice, selectedVehicle, priceInputType]);

 useEffect(() => {
 if (selectedVehicle && formData.proposedPrice > 0)
 validateProposedPrice(formData.proposedPrice);
 }, [selectedVehicle]); // eslint-disable-line react-hooks/exhaustive-deps

 const handleAddPaymentPlan = () => {
 if (tempPaymentMethod) {
 const selectedForm = paymentForms.find((form) => form.id === tempPaymentMethod);
 const optionDetails = selectedForm?.options?.find((opt) => opt.id === tempPaymentOption);
 const initialValue = Math.max(0, parseCurrency(tempPaymentAmountFormatted));
 setSavedPaymentPlans([
 ...savedPaymentPlans,
 {
 paymentMethodId: tempPaymentMethod,
 optionId: tempPaymentOption || undefined,
 value: initialValue,
 installments: optionDetails?.installments,
 date: new Date().toISOString().split("T")[0],
 },
 ]);
 setTempPaymentMethod("");
 setTempPaymentOption("");
 setTempPaymentAmountFormatted("");
 }
 };

 const paymentMethodsOptions = paymentForms.map((form) => ({ value: form.id, label: form.name }));

 const financingOptionsList = React.useMemo(() => {
 if (tempPaymentMethod) {
 const selectedForm = paymentForms.find((form) => form.id === tempPaymentMethod);
 if (selectedForm?.options && selectedForm.options.length > 0) {
 const activeOptions = selectedForm.options.filter((option) => option.isActive !== false);
 if (activeOptions.length > 0)
 return activeOptions.map((option) => ({ value: option.id, label: option.name }));
 }
 }
 return [];
 }, [paymentForms, tempPaymentMethod]);

 const commitProposal = () => {
 if (selectedVehicle) {
 const minimumPrice = getVehicleMinimumPrice(selectedVehicle);
 if (minimumPrice > 0 && formData.proposedPrice < minimumPrice) {
 setPriceError(t("proposals.form.invalidNegotiatedPrice"));
 return;
 }
 }

 const hasInvalidPayment = savedPaymentPlans.some((plan) => !plan.value || plan.value <= 0);
 if (hasInvalidPayment) {
 alert(t("proposals.form.paymentsMustBePositive"));
 return;
 }

 if (!isFinanciallyBalanced()) {
 alert(t("proposals.form.paymentsMustCoverFinalPrice"));
 return;
 }

 const benefitsData = selectedBenefits
 .map((benefitId) => {
 const benefit = benefits.find((b) => b.id === benefitId);
 if (!benefit) return null;
 const raw: string | number = benefit.value as string | number;
 const val =
 typeof raw === "string"
 ? parseFloat(raw.replace(/[^\d,.-]/g, "").replace(",", ".")) || 0
 : Number(raw) || 0;
 const hasFullDiscount = benefitsWithFullDiscount.includes(benefitId);
 const discountPct = hasFullDiscount ? 100 : 0;
 const discountVal = hasFullDiscount ? val : 0;
 return {
 id: benefit.id,
 value: val,
 totalWithDiscount: val - discountVal,
 discountPercentage: discountPct,
 discountValue: discountVal,
 };
 })
 .filter(Boolean) as Array<{
 id: string;
 value: number;
 totalWithDiscount: number;
 discountPercentage: number;
 discountValue: number;
 }>;

 const chargesData = selectedCharges
 .map((chargeId) => {
 const charge = charges.find((c) => c.id === chargeId && c.isActive);
 if (!charge) return null;
 const basePrice = Number(formData.proposedPrice) || 0;
 const benefitsTotal = calculateBenefitsTotal();
 const priceForPct = basePrice + benefitsTotal;
 let originalValue = 0;
 if (charge.isPercentage && priceForPct > 0) {
 originalValue = (priceForPct * (Number(charge.percentage) || 0)) / 100;
 } else {
 originalValue = Number(charge.amount) || 0;
 }
 const discountPct = chargeDiscounts[chargeId] || 0;
 const discountVal = (originalValue * discountPct) / 100;
 return {
 id: charge.id,
 discountPercentage: discountPct,
 totalValue: originalValue - discountVal,
 discountValue: discountVal,
 };
 })
 .filter(Boolean) as Array<{
 id: string;
 discountPercentage: number;
 totalValue: number;
 discountValue: number;
 }>;

 const paymentsWithoutDate = savedPaymentPlans.map(({ date, ...payment }) => payment);

 onSubmit({
 ...formData,
 finalPrice: calculateFinalPrice(),
 benefits: benefitsData,
 charges: chargesData,
 payments: paymentsWithoutDate,
 tradeVehicles:
 savedTradeVehicles && savedTradeVehicles.length > 0 ? savedTradeVehicles : undefined,
 });
 };

 const isLastStep = currentStep === STEPS.length - 1;
 const canGoNext = currentStep !== 0 || (!!formData.leadId && !!formData.vehicleId);
 const financialStepBlocked =
 currentStep === 1 &&
 (!selectedVehicle || isPriceInvalid() || Boolean(priceError));
 const tradePaymentStepBlocked = currentStep === 2 && !isFinanciallyBalanced();
 const financialSectionValid =
 Boolean(selectedVehicle) && !isPriceInvalid() && !priceError;
 const canAccessSummaryStep =
 Boolean(formData.leadId) &&
 Boolean(formData.vehicleId) &&
 Boolean(selectedLead?.document && selectedLead.document.trim() !== "") &&
 financialSectionValid &&
 isFinanciallyBalanced();

 const handleStepperClick = (index: number) => {
 if (index === STEPS.length - 1 && !canAccessSummaryStep) {
 alert(t("proposals.form.paymentsMustCoverFinalPrice"));
 return;
 }
 setCurrentStep(index);
 };

 const handleNextStep = () => {
 if (isSubmitting || !canGoNext || financialStepBlocked || tradePaymentStepBlocked) {
 return;
 }
 setTimeout(() => setCurrentStep((s) => s + 1), 0);
 };

 return (
 <form
 onSubmit={(e) => {
 e.preventDefault();
 }}
 className="flex gap-6 items-start"
 >
 {/* ── Main column ── */}
 <div className="flex-1 min-w-0 space-y-6">

 {/* Step indicator card */}
 <div className="bg-card border border-border rounded-xl p-5">
 <div className="flex items-center justify-between mb-5">
 <div>
 <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
 {proposal ? "Editando Proposta" : "Nova Proposta"}
 </p>
 {selectedVehicle && (
 <p className="text-sm font-semibold text-foreground font-display mt-0.5">
 {getVehicleName(selectedVehicle)}
 {selectedLead && (
 <span className="font-normal text-muted-foreground"> · {selectedLead.name}</span>
 )}
 </p>
 )}
 </div>
 <span className="text-xs text-muted-foreground tabular-nums">
 {currentStep + 1} / {STEPS.length}
 </span>
 </div>

 <div className="relative flex items-start justify-between">
 <div className="absolute left-0 right-0 top-5 h-px bg-border -z-0" />
 {STEPS.map((step, index) => {
 const Icon = step.icon;
 const isDone = index < currentStep;
 const isActive = index === currentStep;
 return (
 <button
 key={step.id}
 type="button"
 onClick={() => handleStepperClick(index)}
 className="relative flex flex-col items-center gap-2 group z-10 flex-1"
 >
 <div className={cn(
 "w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-200",
 isDone && "bg-emerald-500 border-emerald-500 text-white",
 isActive && "bg-card dark:bg-slate-900 border-accent text-accent shadow-sm ring-4 ring-accent/10",
 !isDone && !isActive && "bg-card dark:bg-slate-900 border-border text-muted-foreground group-hover:border-accent/60 group-hover:text-accent/80"
 )}>
 {isDone ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
 </div>
 <span className={cn(
 "text-[0.65rem] font-semibold uppercase tracking-wide text-center leading-tight px-1 hidden sm:block",
 isActive ? "text-accent" : "text-muted-foreground"
 )}>
 {step.label}
 </span>
 </button>
 );
 })}
 </div>
 </div>

 {/* Step content */}
 {currentStep === 0 && (
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
 <ClientSection
 leadId={formData.leadId}
 selectedLead={selectedLead}
 onLeadChange={(leadId) => handleInputChange("leadId", leadId)}
 />
 <VehicleSection
 vehicleId={formData.vehicleId}
 selectedVehicle={selectedVehicle}
 onVehicleChange={(vehicleId) => handleInputChange("vehicleId", vehicleId)}
 onVehicleRemove={() => {
 handleInputChange("vehicleId", "");
 setSelectedVehicle(null);
 }}
 />
 </div>
 )}

 {currentStep === 1 && (
 <FinancialProposalSection
 selectedVehicle={selectedVehicle}
 vehicleValueFormatted={vehicleValueFormatted}
 onVehicleValueChange={handleVehicleValueChange}
 onApplyAdvertisedVehicleValue={handleApplyAdvertisedVehicleValue}
 priceInputType={priceInputType}
 onPriceInputTypeChange={(type) => {
 setPriceInputType(type);
 if (type === "value") setDiscountInputFormatted("");
 else if (selectedVehicle && formData.proposedPrice > 0) {
 const originalPrice = getVehiclePrice(selectedVehicle);
 if (originalPrice > 0)
 setDiscountInputFormatted(formatPercentageInput(discountPercentage));
 }
 }}
 proposedPriceFormatted={proposedPriceFormatted}
 onProposedPriceChange={handleProposedPriceChange}
 discountInputFormatted={discountInputFormatted}
 onDiscountInputChange={handleDiscountInputChange}
 priceError={priceError}
 isPriceInvalid={isPriceInvalid}
 proposedPrice={formData.proposedPrice}
 discount={discount}
 discountPercentage={discountPercentage}
 benefits={benefits}
 selectedBenefits={selectedBenefits}
 onBenefitsChange={setSelectedBenefits}
 benefitsWithFullDiscount={benefitsWithFullDiscount}
 onBenefitsFullDiscountChange={setBenefitsWithFullDiscount}
 charges={charges}
 selectedCharges={selectedCharges}
 onChargesChange={setSelectedCharges}
 chargeDiscounts={chargeDiscounts}
 onChargeDiscountChange={(chargeId, discountValue) =>
 setChargeDiscounts((prev) => ({ ...prev, [chargeId]: discountValue }))
 }
 chargesWithDiscountEnabled={chargesWithDiscountEnabled}
 onChargesDiscountEnabledChange={setChargesWithDiscountEnabled}
 getVehiclePrice={getVehiclePrice}
 getChargeOriginalValue={getChargeOriginalValue}
 getChargeValue={getChargeValue}
 calculateBenefitsTotal={calculateBenefitsTotal}
 calculateChargesTotal={calculateChargesTotal}
 />
 )}

 {currentStep === 2 && (
 <div className="space-y-6">
 <TradeVehiclesSection
 tradeVehicles={tradeVehicles || null}
 savedTradeVehicles={savedTradeVehicles || null}
 brands={brands}
 models={models}
 vehicleOptions={vehicleOptions}
 onTradeVehiclesChange={setTradeVehicles}
 onSavedTradeVehiclesChange={setSavedTradeVehicles}
 />
 <PaymentsSection
 savedPaymentPlans={savedPaymentPlans}
 tempPaymentMethod={tempPaymentMethod}
 tempPaymentOption={tempPaymentOption}
 tempPaymentAmountFormatted={tempPaymentAmountFormatted}
 paymentForms={paymentForms}
 isLoadingPaymentData={isLoadingPaymentData}
 selectedVehicle={selectedVehicle}
 financingOptionsList={financingOptionsList}
 paymentMethodsOptions={paymentMethodsOptions}
 onSavedPaymentPlansChange={setSavedPaymentPlans}
 onTempPaymentMethodChange={(value) => {
 setTempPaymentMethod(value);
 setTempPaymentOption("");
 }}
 onTempPaymentOptionChange={setTempPaymentOption}
 onTempPaymentAmountChange={setTempPaymentAmountFormatted}
 calculateRemainingAmount={calculateRemainingAmount}
 calculatePaymentWithInterest={calculatePaymentWithInterest}
 handleAddPaymentPlan={handleAddPaymentPlan}
 />
 </div>
 )}

 {currentStep === 3 && (
 <ProposalDetailsSection
 validUntil={formData.validUntil || ""}
 deliveryDate={formData.deliveryDate || ""}
 notes={formData.notes || ""}
 onValidUntilChange={(isoDate) => handleInputChange("validUntil", isoDate)}
 onDeliveryDateChange={(isoDate) => handleInputChange("deliveryDate", isoDate)}
 onNotesChange={(notes) => handleInputChange("notes", notes)}
 />
 )}

 {/* Navigation */}
 <div className="flex items-center justify-between pt-4 border-t border-border">
 <div className="flex items-center gap-2">
 <Button
 type="button"
 variant="ghost"
 onClick={onCancel}
 disabled={isSubmitting}
 className="text-muted-foreground"
 >
 Cancelar
 </Button>
 {currentStep > 0 && (
 <Button
 type="button"
 variant="outline"
 onClick={() => setCurrentStep((s) => s - 1)}
 disabled={isSubmitting}
 >
 <ChevronLeft className="h-4 w-4 mr-1" />
 Anterior
 </Button>
 )}
 </div>

 <div className="flex items-center gap-3">
 <span className="text-xs text-muted-foreground tabular-nums hidden sm:block">
 {currentStep + 1} / {STEPS.length}
 </span>
 {isLastStep ? (
 <Button
 type="button"
 onClick={() => commitProposal()}
 disabled={
 isSubmitting ||
 !formData.leadId ||
 !formData.vehicleId ||
 !selectedLead?.document ||
 selectedLead.document.trim() === "" ||
 isPriceInvalid() ||
 !isFinanciallyBalanced()
 }
 className="min-w-[140px]"
 >
 {isSubmitting ? "Salvando..." : proposal ? "Salvar Proposta" : "Criar Proposta"}
 </Button>
 ) : (
 <Button
 type="button"
 onClick={handleNextStep}
 disabled={
 isSubmitting ||
 !canGoNext ||
 financialStepBlocked ||
 tradePaymentStepBlocked
 }
 >
 Próxima Etapa
 <ChevronRight className="h-4 w-4 ml-1" />
 </Button>
 )}
 </div>
 </div>
 </div>

 {/* ── Sidebar: Financial Summary ── */}
 <FinancialSummary
 selectedVehicle={selectedVehicle}
 proposedPrice={formData.proposedPrice}
 selectedBenefits={selectedBenefits}
 benefits={benefits}
 benefitsWithFullDiscount={benefitsWithFullDiscount}
 selectedCharges={selectedCharges}
 charges={charges}
 chargeDiscounts={chargeDiscounts}
 savedTradeVehicles={savedTradeVehicles || null}
 savedPaymentPlans={savedPaymentPlans}
 brands={brands}
 models={models}
 getVehiclePrice={getVehiclePrice}
 getChargeOriginalValue={getChargeOriginalValue}
 getChargeValue={getChargeValue}
 calculateBenefitsTotal={calculateBenefitsTotal}
 calculateChargesTotal={calculateChargesTotal}
 calculateTradeVehiclesTotal={calculateTradeVehiclesTotal}
 calculateFinalPrice={calculateFinalPrice}
 calculateTotalPaid={calculateTotalPaid}
 calculateRemainingAmount={calculateRemainingAmount}
 />
 </form>
 );
};
